import { DataTablePageEvent } from 'primereact/datatable';
import { DropdownProps } from 'primereact/dropdown';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { PaginatorPageChangeEvent } from 'primereact/paginator';
import { OfferStatusEnum, Page } from '../api/_generated_';
import { Sort } from '../components/types/types';
import { OFFER_LIST } from '../constants/queryKeys';
import { getSortOrder } from '../utils/helper';
import useAxiosPrivate from './useAxiosPrivate';
import useDebounce from './useDebounce';

const STORAGE_KEY = 'offers_filters';

type Filters = {
  page: number;
  rows: number;
  keyword: string;
  sortField: string;
  sortOrder: number;
  status: OfferStatusEnum | null;
};

export default function useOffers() {
  const axios = useAxiosPrivate();

  const storedFilters = useMemo<Filters>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw
        ? JSON.parse(raw)
        : {
          page: 0,
          rows: 20,
          keyword: '',
          sortField: 'createdAt',
          sortOrder: -1,
          status: null,
        };
    } catch {
      return {
        page: 0,
        rows: 20,
        keyword: '',
        sortField: 'createdAt',
        sortOrder: -1,
        status: null,
      };
    }
  }, []);

  const [filters, setFilters] = useState<Filters>(storedFilters);

  const debouncedKeyword = useDebounce(filters.keyword, 500);

  async function getOffers({ queryKey }: any): Promise<Page> {
    const {
      page,
      rows,
      keyword,
      sort,
      status,
    } = queryKey[1];

    const orderValue = getSortOrder(sort.order);
    let queryString = `/offers?page=${page}&size=${rows}&keyword=${keyword}&sort=${sort.field},${orderValue}`;

    if (status) {
      queryString += `&status=${status}`;
    }

    const res = await axios.get<Page>(queryString);
    return res.data;
  }

  const { data: result, isFetching, status: queryStatus } = useQuery({
    queryKey: [
      OFFER_LIST,
      {
        page: filters.page,
        rows: filters.rows,
        keyword: debouncedKeyword,
        sort: { field: filters.sortField, order: filters.sortOrder },
        status: filters.status,
      },
    ],
    queryFn: getOffers,
    keepPreviousData: true,
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  const updateFilters = (updates: Partial<Filters>) => {
    setFilters((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const onChangePage = useCallback(
    (e: PaginatorPageChangeEvent) => {
      updateFilters({ page: e.page, rows: e.rows });
    },
    [],
  );

  const onChangeKeyword = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateFilters({ keyword: e.target.value, page: 0 });
  };

  const onChangeStatus = ({ value }: DropdownProps) => {
    updateFilters({ status: value ?? null, page: 0 });
  };

  const handleClearFilter = () => {
    const cleared: Filters = {
      page: 0,
      rows: filters.rows,
      keyword: '',
      sortField: filters.sortField,
      sortOrder: filters.sortOrder,
      status: null,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cleared));
    setFilters(cleared);
  };

  const onSort = useCallback(
    (e: DataTablePageEvent) => {
      updateFilters({
        sortField: e.sortField,
        sortOrder: e.sortOrder,
        page: 0,
      });
    },
    [],
  );

  return {
    isFetching,
    queryStatus,
    sort: { field: filters.sortField, order: filters.sortOrder } as Sort,
    keyword: filters.keyword,
    status: filters.status,
    pageState: {
      first: filters.page * filters.rows,
      rows: filters.rows,
      page: filters.page,
      pageCount: 0,
    },
    result,
    onChangeStatus,
    onChangePage,
    onChangeKeyword,
    handleClearFilter,
    onSort,
  };
}
